import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../AiWebForm.styles";
import { dayLabel } from "../AiWebFormHelper";
import { ChatEntry } from "../AiWebFormTypesAndValidation";
import { AiChatEntry, AiStartingChecklist, AiWorking } from "./AiChatEntries";
import {
  AlertGlyph,
  ChevronDownGlyph,
  CloseGlyph,
  InfoGlyph,
  MoreGlyph,
  RefreshGlyph,
  SparkleGlyph,
} from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface Props {
  width: number | string;
  /** The Try chips scroll sideways in one row (all but large screens), leaving the conversation its height. */
  compact: boolean;
  entries: ChatEntry[];
  starting: boolean;
  startingStep: number;
  startingStyle?: string;
  turnRunning: boolean;
  stale: boolean;
  onDismissStale: () => void;
  /** The template's fields changed after the design was made. */
  fieldsChanged: boolean;
  onBuildNew: () => void;
  hasEarlier: boolean;
  loadingEarlier: boolean;
  onLoadEarlier: () => void;
  onOpenVersion: (version: number) => void;
  /** Puts text in the composer (a Try chip, a suggestion, "Try again"). */
  onSuggest: (text: string) => void;
  canEdit: boolean;
  onHide: (() => void) | null;
  composer: ReactNode;
}

export const AiChatPanel = ({
  width,
  compact,
  entries,
  starting,
  startingStep,
  startingStyle,
  turnRunning,
  stale,
  onDismissStale,
  fieldsChanged,
  onBuildNew,
  hasEarlier,
  loadingEarlier,
  onLoadEarlier,
  onOpenVersion,
  onSuggest,
  canEdit,
  onHide,
  composer,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const lastId = entries.length ? entries[entries.length - 1].id : "";

  // Whether the reader is at the newest line; scrolled up to read, they stay where they are.
  const atBottomRef = useRef(true);

  // New lines arrive at the bottom: keep it in view (older pages load above, without a jump).
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    atBottomRef.current = true;
    // A short window scrolls the page instead (the log is not a scroller there): bring the newest line in.
    const newest = node.firstElementChild?.lastElementChild;
    if (node.scrollHeight <= node.clientHeight + 1 && newest) newest.scrollIntoView({ block: "nearest" });
  }, [lastId, turnRunning, starting]);

  // A resize or a reflow (the window, a banner, wrapped text) keeps the newest line in view too.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) node.scrollTop = node.scrollHeight;
    });
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    return () => observer.disconnect();
  }, []);

  const onScroll = () => {
    const node = scrollRef.current;
    if (node) atBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
  };

  const busy = turnRunning || starting || !canEdit;

  return (
    <Box sx={styles.chat(width)} component="section" aria-label={copy.designerTitle}>
      <Box sx={styles.chatHeader}>
        <Box sx={styles.designerTile} aria-hidden="true">
          <SparkleGlyph />
        </Box>
        <Box sx={styles.chatHeaderText}>
          <Box component="h2" sx={styles.panelTitle}>
            {copy.designerTitle}
          </Box>
          <Box sx={styles.panelSubtitle}>{copy.designerSubtitle}</Box>
        </Box>
        {onHide && (
          <>
            <Box
              component="button"
              type="button"
              sx={styles.moreButton}
              aria-label={copy.moreActions}
              aria-haspopup="menu"
              aria-expanded={menuAnchor ? true : undefined}
              onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
            >
              <MoreGlyph />
            </Box>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onHide();
                }}
              >
                {copy.removeFromList}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box sx={styles.chatNotices}>
          <Box sx={styles.boundaries}>
            <InfoGlyph />
            <span>{copy.boundaries}</span>
          </Box>
          {stale && (
            <Box sx={styles.staleBanner} role="alert">
              <RefreshGlyph />
              <Box sx={styles.bannerText}>{copy.staleBanner}</Box>
              <Box component="button" type="button" sx={styles.bannerClose} aria-label={copy.dismiss} onClick={onDismissStale}>
                <CloseGlyph />
              </Box>
            </Box>
          )}
          {fieldsChanged && (
            <Box sx={styles.fieldsBanner} role="alert">
              <AlertGlyph />
              <Box sx={styles.bannerText}>{copy.fieldsChanged}</Box>
              <Button size="small" variant="outlined" onClick={onBuildNew}>
                {copy.buildNew}
              </Button>
            </Box>
          )}
      </Box>

      <Box sx={styles.chatScroll} ref={scrollRef} onScroll={onScroll}>

        <Box sx={styles.log} role="log" aria-live="polite" aria-label={copy.chatLog}>
          {hasEarlier && (
            <Box sx={styles.earlier}>
              <Box component="button" type="button" sx={styles.earlierButton} onClick={onLoadEarlier} disabled={loadingEarlier}>
                <ChevronDownGlyph sx={{ transform: "rotate(180deg)" }} />
                {copy.showEarlier}
              </Box>
            </Box>
          )}
          {entries.length > 0 && !hasEarlier && <Box sx={styles.dayDivider}>{dayLabel(entries[0].at)}</Box>}
          {entries.map((entry) => (
            <AiChatEntry key={entry.id} entry={entry} onOpenVersion={onOpenVersion} onSuggest={onSuggest} busy={busy} />
          ))}
          {starting && <AiStartingChecklist step={startingStep} styleText={startingStyle} />}
          {turnRunning && <AiWorking />}
        </Box>
      </Box>

      <Box sx={styles.tryArea}>
        <Box sx={styles.tryHeading}>{copy.tryHeading}</Box>
        <Box sx={styles.tryChips(compact)}>
          {copy.tryChips.map((chip) => (
            <Box key={chip} component="button" type="button" sx={styles.tryChip} disabled={!canEdit || starting} onClick={() => onSuggest(chip)}>
              {chip}
            </Box>
          ))}
        </Box>
      </Box>
      {composer}
    </Box>
  );
};
